import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  ProductCard,
  PRODUCT_CAROUSEL_ITEM_CLASSNAME,
  PRODUCT_CAROUSEL_TRACK_CLASSNAME,
  getProductCarouselScrollStep,
} from "@/features/instantsearch/components";
import { GradualBlur } from "@/components/GradualBlur";
import { ProductCarouselSkeleton } from "@/components/ProductCardSkeleton";
import { useOrderRecommendations } from "../hooks/use-order-recommendations";

export const ProductRecommendationsSection: React.FC = () => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Fetch order recommendations from API
  const { recommendations, isLoading } = useOrderRecommendations();

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({
      left: -getProductCarouselScrollStep(),
      behavior: "smooth",
    });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({
      left: getProductCarouselScrollStep(),
      behavior: "smooth",
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Recommendations Section */}
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between px-2">
          <h2 className="text-xs md:text-sm lg:text-base font-semibold leading-[30px] text-text-primary dark:text-text-primary">
            {t("orders.detail.recommendations.title")}
          </h2>

          {/* Navigation arrows */}
          <div className="flex items-center gap-2">
            <button
              onClick={scrollLeft}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-border-secondary bg-white hover:bg-bg-secondary dark:border-border-primary dark:bg-bg-secondary dark:hover:bg-bg-tertiary sm:h-8 sm:w-8 transition-colors"
              aria-label={t("common.previous")}
            >
              <ChevronLeft className="h-3.5 w-3.5 text-text-primary dark:text-text-primary sm:h-4 sm:w-4" />
            </button>
            <button
              onClick={scrollRight}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-border-secondary bg-white hover:bg-bg-secondary dark:border-border-primary dark:bg-bg-secondary dark:hover:bg-bg-tertiary sm:h-8 sm:w-8 transition-colors"
              aria-label={t("common.next")}
            >
              <ChevronRight className="h-3.5 w-3.5 text-text-primary dark:text-text-primary sm:h-4 sm:w-4" />
            </button>
            <button className="hidden sm:inline-block rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-black hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-750  sm:px-4 sm:py-2 sm:text-sm">
              {t("shopping.brands.viewAll")}
            </button>
          </div>
        </div>

        {/* Products Carousel or Loading */}
        {isLoading ? (
          // Loading Skeleton - Professional ProductCard placeholders
          <ProductCarouselSkeleton count={6} />
        ) : (
          // Products Carousel with Overlay
          recommendations &&
          recommendations.length > 0 && (
            <div className="relative">
              <div
                ref={scrollContainerRef}
                className={PRODUCT_CAROUSEL_TRACK_CLASSNAME}
              >
                {/* Left Spacer - İlk card'ın yarısını göstermek için (mobile only) */}
                <div className="flex-shrink-0 w-[100px] sm:hidden" />

                {recommendations.map((product) => {
                  const productId = product.productId || product.id || "";
                  return (
                    <div
                      key={productId}
                      className={PRODUCT_CAROUSEL_ITEM_CLASSNAME}
                    >
                      <ProductCard product={product} />
                    </div>
                  );
                })}

                {/* Right Spacer - Son card'ın yarısını göstermek için (mobile only) */}
                <div className="flex-shrink-0 w-[100px] sm:hidden" />
              </div>
              <GradualBlur containerRef={scrollContainerRef} />
            </div>
          )
        )}
      </div>
    </div>
  );
};
